/**
 * AdminUsers
 *
 * Landing page for user-related admin tools: User Management, Analytics,
 * Newsletter Lists, and Free Users. Redirects non-admins.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import Footer from '../../../components/shared/Footer';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { Users, UserCheck, Gift, ArrowLeft, TrendingUp, BarChart3, Shield, List } from 'lucide-react';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function AdminUsers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  
  // Check if user is the admin using the same pattern as other admin pages
  const isAdmin = isUserAdmin(user);

  // Check if user is an admin, if not, redirect to home page
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      }
      setIsLoading(false);
    } else if (user === null) {
      // No user, redirect to login
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  // User admin pages
  const userPages = [
    {
      title: 'User Management',
      description: 'View comprehensive user data, subscriptions, and activity metrics',
      icon: UserCheck,
      path: '/app/private/userManagement',
      color: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300',
    },
    {
      title: 'Users List',
      description: 'Browse and search all users with filters and sorting',
      icon: List,
      path: '/app/private/admin/users-list',
      color: 'bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-300',
    },
    {
      title: 'Business Analytics Dashboard',
      description: 'View comprehensive business metrics, revenue trends, and customer analytics',
      icon: BarChart3,
      path: '/app/private/analytics',
      color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
    },
    {
      title: 'Beehiiv Newsletter Emails',
      description: 'View segregated email lists for newsletter management with CSV export functionality',
      icon: TrendingUp,
      path: '/app/private/admin/customer-lists',
      color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
    },
    {
      title: 'Free Users',
      description: 'Manage email addresses that receive free premium subscriptions',
      icon: Gift,
      path: '/app/private/freeUsers',
      color: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300',
    }
    ,
    {
      title: 'Admin Management',
      description: 'Grant or revoke admin access by UID or email',
      icon: Shield,
      path: '/app/private/admin/claims',
      color: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300',
    }
  ];

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading users admin..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Redirect happens in useEffect
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto pb-20">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate('/app/private/admin')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mr-4 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded-lg p-1"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span className="text-sm font-medium">Back to Admin</span>
          </button>
        </div>
        <div className="flex items-center mb-4">
          <Users className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">User Management</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Manage user accounts and free subscriptions.
        </p>
      </div>

      {/* User pages grid */}
      <div className="flex flex-col gap-4">
        {userPages.map((page, index) => (
          <button
            key={page.path || `user-page-${index}`}
            onClick={page.path ? () => navigate(page.path) : () => {}}
            className="flex items-center w-full p-6 bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            <div className={`p-2 rounded-lg ${page.color} mr-4`}>
              <page.icon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {page.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {page.description}
              </p>
            </div>
            <div className="w-6 flex items-center justify-center h-5">
              <svg className="h-4 w-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        ))}
      </div>

      <Footer />
    </div>
  );
} 